/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { ModalConfirm } from "@nordcloud/gnui";
import { useAddEnvironmentsToOrgUnitMutation } from "~/generated/graphql";
import { GlobalSpinner, ReactTable } from "~/components";
import { useMutationHandler } from "~/hooks";
import { isNotEmpty, Maybe } from "~/tools";
import { ConflictsListColumns } from "./ConflictsListColumns";
import { Environment } from "./utils";

type Props = {
  attachmentConflicts: Maybe<Environment>[];
  onClose: () => void;
  onAttached: () => void;
  structureId: string;
  orgUnitId: string;
};

export function ConflictsList({
  attachmentConflicts,
  onClose,
  onAttached,
  structureId,
  orgUnitId,
}: Props) {
  const [addEnvironmentsToOrgUnit, { loading }] =
    useAddEnvironmentsToOrgUnitMutation();
  const { runMutation } = useMutationHandler();

  const onAttach = async () => {
    const envIds = attachmentConflicts.flatMap((env) =>
      env?.nid ? [env.nid] : []
    );

    await runMutation({
      mutation: () =>
        addEnvironmentsToOrgUnit({
          variables: {
            input: {
              orgUnitId,
              envIds,
            },
          },
        }),
      successText: `${envIds.length} environments have been attached.`,
    });

    onAttached();
  };

  const columns = React.useMemo(
    () => ConflictsListColumns(structureId),
    [structureId]
  );
  const isModalConfirmOpen = isNotEmpty(attachmentConflicts);

  return (
    <>
      <GlobalSpinner loading={loading} />
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={onAttach}
        actionLabel="Attach"
        contentLabel="Attachment conflicts"
        onClose={onClose}
      >
        <ReactTable
          small
          striped
          columns={columns}
          data={attachmentConflicts}
        />
      </ModalConfirm>
    </>
  );
}
